/* eslint-disable no-case-declarations */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { ChangeEvent } from 'react'

const FLEX_START = 'flex-start'
const OWNER_FIRST_NAME_FIELD = 'owner.firstName'
import {
  Card,
  makeStyles,
  ActionButton,
  Button,
  Checkbox,
  ConfirmationDialog,
  Grid,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
  motion,
  useIsMobile
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import {
  generateTimeOptions,
  EstimatorAvatar,
  PreviewPopper,
  ResultsTable,
  ResultsTableColumnSelect,
  useClientOptions,
  useDialogs,
  useUser,
  useUserPermissions,
  useUsers
} from '@paintscout/ui-business'
import type { Auth0UserWithClaims, TaskRow, SelectableColumn } from '@paintscout/ui-business'
import { useSnackbar } from 'notistack'
import type { User, Task } from '@paintscout/api'
import { useTaskMutations } from 'shared/hooks'
import { formatName, formatPhoneNumber, isUserAssigned, isUserOwner } from '@paintscout/util/builder'
import type { DealDocument, OptionsDocument } from 'paintscout'
import { Sentry } from 'src/util/appSentry'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import moment from 'moment'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import classNames from 'classnames'
import { contactMethods } from 'src/components/forms/ContactForm'
import { TaskDialog } from 'src/components/dialogs'
import AddIcon from '@material-ui/icons/Add'
import { escapeHref, getActivityColor, removeAuth0Prefix } from '@paintscout/util'
import { useLocation } from 'react-router'
import { getUrlQuery, setUrlQuery } from 'shared/util/routing'
import striptags from 'striptags'
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn'
import { goToDeal } from 'src/util/routing'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {
      overflow: 'hidden'
    },
    card: {
      border: 'none'
    },
    cardContents: {
      overflowX: 'auto',
      transform: 'scaleY(-1)',
      '& > *': {
        transform: 'scaleY(-1)'
      },
      // flip the box shadow
      boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.1)'
    },
    titleContainer: {
      paddingLeft: theme.spacing(2),
      paddingTop: theme.spacing(1),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%'
    },
    tableRow: {
      cursor: 'pointer',
      borderBottom: `1px solid ${theme.palette.divider}`,
      verticalAlign: 'middle',
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P98
      }
    },
    calendarIcon: {
      fontSize: '1rem',
      marginRight: theme.spacing(0.5),
      marginTop: theme.spacing(0.25)
    },

    mobileCellDiv: {
      display: 'flex',
      alignItems: FLEX_START,
      paddingTop: theme.spacing(1.5),
      paddingLeft: theme.spacing()
    },
    mobileCheckbox: {
      paddingBottom: theme.spacing(2),
      maxWidth: '30px',
      [theme.breakpoints.down('xs')]: {
        paddingLeft: '0px !important'
      }
    },
    dashCheckbox: {
      paddingBottom: theme.spacing(4),
      [theme.breakpoints.down('lg')]: {
        paddingLeft: `${theme.spacing(0.5)}px !important`
      },
      [theme.breakpoints.down('md')]: {
        paddingLeft: `${theme.spacing(0)}px !important`
      }
    },
    mobileTitleCell: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    smTitleCell: {
      maxWidth: '280px',
      minWidth: '280px',
      [theme.breakpoints.down('lg')]: {
        maxWidth: '180px',
        minWidth: '180px'
      }
    },
    mobileIconCell: {
      padding: theme.spacing(),
      marginRight: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        maxWidth: '50px'
      }
    },
    titleCell: {
      maxWidth: '240px',
      minWidth: '240px',
      [theme.breakpoints.down('lg')]: {
        maxWidth: '180px',
        minWidth: '180px'
      },
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
      whiteSpace: 'normal'
    },
    cellText: {
      display: 'flex',
      alignItems: FLEX_START,
      flexWrap: 'wrap',
      wordBreak: 'break-word',
      overflowWrap: 'anywhere'
    },
    emailCell: {
      whiteSpace: 'nowrap'
    },
    dueCell: {
      minWidth: '240px'
    },
    mobileDueCell: {
      flexDirection: 'column'
    },
    smDateCell: {
      width: '100%'
    },
    mobileDateCell: {
      minWidth: '80px',
      maxWidth: '80px',
      paddingLeft: `${theme.spacing(0.25)}px !important`,
      [theme.breakpoints.up('lg')]: {
        paddingLeft: `${theme.spacing()}px !important`
      }
    },
    completedText: {
      color: theme.paintscout.palette.brand.Neutral.P80,
      textDecoration: 'line-through'
    },
    overdueText: {
      color: theme.palette.error.main
    },
    titleTable: {
      paddingLeft: `${theme.spacing()}px !important`
    },
    taskDocItem: {
      display: 'flex',
      justifyContent: FLEX_START,
      alignItems: 'center',
      padding: theme.spacing(2),
      paddingLeft: theme.spacing(0.5),
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: theme.paintscout.palette.brand.Neutral.P96
      },
      '&:not(:last-child)': {
        borderBottom: `2px solid ${theme.paintscout.palette.brand.Neutral.P90}`
      }
    },
    checkbox: { padding: '1px', marginRight: '8px', marginLeft: '8px' }
  }),
  { name: 'TaskTable' }
)

export interface TaskTableProps {
  classes?: StyleClasses<typeof useStyles>
  loading?: boolean
  totalRows: number
  rowsPerPage: number
  page: number
  pages: number
  dense?: boolean
  dashboard?: boolean
  enableFade?: boolean
  view?: 'upcoming' | 'overdue' | 'completed' | 'all' // used with enableFade to determine which tasks to fade
  tasks: Task[]
  allowCustomColumns?: boolean
  hideHeaders?: {
    [key: string]: boolean
  }
  sort?: string
  actions?: {
    edit?: boolean
    delete?: boolean
    viewDeal?: boolean
  }
  title?: string
  handleCreate?: () => any
  actionsForRow?: (row: Task) => {
    edit?: boolean
    archive?: boolean
    unarchive?: boolean
    viewDeal?: boolean
    invoice?: boolean
    copy?: boolean
    delete?: boolean
  }

  onlyEditOwn?: boolean
  onRequestSort?: (ev: React.MouseEvent, name: string, direction: 'asc' | 'desc') => void
  onDelete?: (ids: string[]) => any
  onEdit?: (task: Task) => any
  onComplete?: (task: Task) => any
  onFetchNextPage?: (page: number) => any
  onPageChange?: (page: number) => any
  disablePagination?: boolean
  noResultsMessage?: string
}

export type TaskResult = Partial<Task>

function TaskTable({
  tasks,
  actions = {},
  loading,
  onFetchNextPage,
  hideHeaders = {},
  onEdit,
  allowCustomColumns,
  onlyEditOwn,
  onPageChange,
  onRequestSort,
  onDelete,
  rowsPerPage,
  dense,
  dashboard,
  enableFade,
  view,
  title,
  sort = 'startDateTime',
  onComplete,
  handleCreate,
  noResultsMessage,
  ...otherProps
}: TaskTableProps) {
  const classes = useStyles(otherProps)
  const localStorageColumns = JSON.parse(localStorage.getItem('taskTableColumns'))
  const location = useLocation()
  const { pathname, search } = location
  const { users, userPictures } = useUsers()
  const urlParams = getUrlQuery<{ id: string; tab: string }>({ location })

  const defaultColumns: SelectableColumn[] = [
    { name: 'Task', field: 'title', sortable: true, selected: !hideHeaders['title'] || true },
    { name: 'Due Date', field: 'startDateTime', sortable: true, selected: !hideHeaders['startDateTime'] || true },
    {
      name: 'Contact',
      field: 'contactName',
      sortable: false,
      selected: !hideHeaders['contactName'] || true,
      minWidth: 140
    },
    {
      name: 'Preferred',
      field: 'contactPreferred',
      sortable: false,
      selected: !hideHeaders['contactPreferred'] || true
    },
    {
      name: 'Email',
      field: 'contactEmail',
      sortable: false,
      selected: !hideHeaders['contactEmail'] || true,
      minWidth: 200
    },
    { name: 'Phone Number', field: 'contactPhone', sortable: false, selected: !hideHeaders['contactPhone'] || true },
    {
      name: 'Deal Name',
      field: 'dealName',
      sortable: false,
      selected: !hideHeaders['dealName'] || false,
      minWidth: 180
    },
    {
      name: 'Deal Address',
      field: 'dealAddress',
      sortable: false,
      selected: !hideHeaders['dealAddress'] || false,
      minWidth: 180
    },
    {
      name: 'User',
      field: OWNER_FIRST_NAME_FIELD,
      sortable: true,
      selected: !hideHeaders[OWNER_FIRST_NAME_FIELD] || true
    }
  ].map((column) => ({
    ...(column as SelectableColumn),
    selected: localStorageColumns && allowCustomColumns ? localStorageColumns.includes(column.field) : column.selected
  }))
  const [columns, setColumns] = useState<SelectableColumn[]>(defaultColumns)

  const deleteTimeouts = useRef<{ [taskId: string]: NodeJS.Timeout }>({})
  const [pendingRemoval, setPendingRemoval] = useState<{ [id: string]: boolean }>({})
  const { openDialog, dismissDialog } = useDialogs()
  const { enqueueSnackbar } = useSnackbar()
  const { user, trialExpired } = useUser()
  const { permissionToEditAllTasks, permissionToEditAssignedTasks } = useUserPermissions()
  const isXs = useMediaQuery('xs')
  const isDense = isXs || dense
  const lowerMd = useMediaQuery(768, { up: true })
  const upperMd = useMediaQuery(1024, { up: true })
  const midSize = lowerMd && !upperMd
  const isXLg = useMediaQuery('xl', { up: true }) && dense
  const isSm = useMediaQuery('sm', { up: true }) && !isXs && !isXLg && !dashboard && !midSize
  const mobile = useIsMobile()
  const { options } = useClientOptions()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedTask, setSelectedTask] = useState<TaskRow | null>(null)

  const onPage = (id: string) => {
    return location.pathname.includes(id)
  }

  const taskUsers = users?.reduce(
    (acc, user) => {
      const { __typename, ...rest } = user
      acc[removeAuth0Prefix(user.user_id)] = rest
      return acc
    },
    {} as { [key: string]: User }
  )

  const rows = tasks.map((task: TaskResult) => mapTaskToRow(options, task, user, taskUsers)).filter(Boolean)
  const { deleteTasks, saveTask } = useTaskMutations()

  const handleColumnsChange = (selected: string[]) => {
    localStorage.setItem('taskTableColumns', JSON.stringify(selected))
    setColumns(
      defaultColumns.map((column) => ({
        ...column,
        selected: selected.includes(column.field)
      }))
    )
  }

  // Check url for an id that we want to open in dialog
  useEffect(() => {
    const taskIdMatch = search.match(/&([a-z0-9-]+)\/?$/)
    if (taskIdMatch && taskIdMatch[1]) {
      setUrlQuery({
        location,
        replaceParams: true,
        params: {
          tab: 'tasks',
          search: null,
          filter: null
        }
      })
      const taskId = taskIdMatch[1]
      handleEditTask(taskId)
    }
  }, [])

  const handleClose = () => {
    setAnchorEl(null)
    setSelectedTask(null)
  }
  const handlePageChange = useCallback(
    (page: number) => {
      const nextPageFirstIndex = (page - 1) * rowsPerPage
      const dataExists = !!tasks[nextPageFirstIndex]

      if (onPageChange) {
        onPageChange(page)
      }

      if (!dataExists && onFetchNextPage) {
        onFetchNextPage(page)
      }
    },
    [onPageChange, onFetchNextPage, tasks, rowsPerPage]
  )

  const scheduleTaskForRemoval = (taskId: string) => {
    setPendingRemoval((prev) => ({ ...prev, [taskId]: true }))
    if (deleteTimeouts.current[taskId]) {
      clearTimeout(deleteTimeouts.current[taskId])
    }
    deleteTimeouts.current[taskId] = setTimeout(() => {
      onDelete([taskId])
      setPendingRemoval((prev) => {
        const copy = { ...prev }
        delete copy[taskId]
        return copy
      })
      delete deleteTimeouts.current[taskId]
    }, 2000)
  }

  const cancelTaskRemoval = (taskId: string) => {
    if (deleteTimeouts.current[taskId]) {
      clearTimeout(deleteTimeouts.current[taskId])
      delete deleteTimeouts.current[taskId]
    }
    setPendingRemoval((prev) => ({ ...prev, [taskId]: false }))
  }

  const hasFade = dashboard || (enableFade && view !== 'all')
  const hasReverseFade = enableFade && view === 'completed'

  const rowCheckClick = useCallback(
    async (ev: ChangeEvent<HTMLInputElement>, checked: boolean, task: TaskRow) => {
      ev.stopPropagation()
      const fullTask = tasks.find((t) => t._id === task._id)
      const newTask = {
        ...fullTask,
        completed: checked,
        completedDate: checked ? Date.now().valueOf() : null
      }
      try {
        if (hasFade && onDelete) {
          if ((checked && !hasReverseFade) || (!checked && hasReverseFade)) {
            // becomes checked
            scheduleTaskForRemoval(newTask._id)
          } else {
            // becomes unchecked
            cancelTaskRemoval(newTask._id)
          }
        }
        await saveTask({ task: newTask })
        if (!dashboard) {
          onComplete?.(newTask)
        }
      } catch (error) {
        Sentry.captureException(error)
        enqueueSnackbar('Unable to update Task', { variant: 'error' })
      }
    },
    [tasks, dashboard, onDelete, saveTask, enqueueSnackbar]
  )

  const handleDeleteTask = (taskId: string) => {
    if (anchorEl) {
      setAnchorEl(null)
    }
    openDialog(ConfirmationDialog, {
      message: <span>This will permanently delete this task.</span>,
      onConfirm: async () => {
        try {
          await deleteTasks([taskId])
          if (onDelete) {
            onDelete([taskId])
          }
          dismissDialog()
          setAnchorEl(null)
        } catch (error) {
          console.log(error)
          Sentry.captureException(error, { tags: { taskId: taskId } })
          enqueueSnackbar('Unable to delete Task', { variant: 'error' })
        }
      },
      onCancel: dismissDialog
    })
  }

  const handleEditTask = (taskId: string) => {
    if (anchorEl) {
      setAnchorEl(null)
    }
    if (selectedTask) {
      setSelectedTask(null)
    }
    openDialog(TaskDialog, {
      taskId: taskId,
      onConfirm: (task: Task) => {
        onEdit(task)
        dismissDialog()
        setAnchorEl(null)
        setSelectedTask(null)
      },
      onDelete: (taskId: string) => {
        if (onDelete) {
          onDelete([taskId])
        }
        dismissDialog()
        setAnchorEl(null)
        setSelectedTask(null)
      },
      onCancel: () => {
        dismissDialog()
        setSelectedTask(null)
      }
    })
  }

  useEffect(() => {
    if (urlParams.id) {
      handleEditTask(urlParams.id)
    }
    setUrlQuery({ location, params: { tab: undefined, id: undefined } })
  }, [])

  const handleRowClick = useCallback(
    (ev: React.MouseEvent<HTMLElement>, task: TaskRow) => {
      if ((ev.target as HTMLElement).classList?.contains('disable-click')) {
        return
      }
      if (onlyEditOwn && !task.isOwner) {
        return
      }
      handleEditTask(task._id)
    },
    [tasks]
  )

  const handleDenseRowClick = (e: React.MouseEvent<HTMLTableRowElement>, row: TaskRow) => {
    if ((e.target as HTMLElement).classList?.contains('disable-click')) {
      return
    }
    if (onlyEditOwn && !row.isOwner) {
      return
    }
    if (selectedTask && selectedTask._id === row._id) {
      setAnchorEl(null)
      setSelectedTask(null)
      return
    }
    setAnchorEl(e.currentTarget)
    setSelectedTask(row)
  }

  const handleAction = useCallback(
    (action: string, taskRows: TaskRow[]) => {
      const ids = taskRows.map((task) => task._id)

      if (action === 'edit') {
        handleEditTask(taskRows[0]._id)
      } else if (action == 'viewDeal') {
        const dealId = taskRows[0]?.dealId ?? null
        if (dealId) {
          goToDeal(dealId)
        } else {
          enqueueSnackbar('No Deal associated with this Task', { variant: 'warning' })
        }
      } else if (action === 'delete') {
        openDialog(ConfirmationDialog, {
          message: <span>This will permanently delete {taskRows[0]?.title}.</span>,
          onConfirm: async () => {
            deleteTasks(ids).catch((error) => {
              console.log(error)
              Sentry.captureException(error)
              enqueueSnackbar('Unable to delete Task', { variant: 'error' })
            })
            if (onDelete) {
              onDelete(ids)
            }
            dismissDialog()
          },
          onCancel: dismissDialog
        })
      }
    },
    [handleRowClick, onDelete]
  )

  const rowActions = useCallback(
    (row: TaskRow) => {
      const allowedActions = []

      if (
        actions.edit &&
        (permissionToEditAllTasks || ((row.isOwner || row.isAssigned) && permissionToEditAssignedTasks))
      ) {
        allowedActions.push({
          key: 'edit',
          label: 'Edit',
          icon: EditIcon
        })
      }

      if (actions.viewDeal) {
        allowedActions.push({
          key: 'viewDeal',
          label: 'View Deal',
          icon: MonetizationOnIcon
        })
      }

      if (
        actions.delete &&
        (permissionToEditAllTasks ||
          ((row.isOwner || row.isAssigned) && permissionToEditAssignedTasks) ||
          (onlyEditOwn && row.isOwner))
      ) {
        allowedActions.push({
          key: 'delete',
          label: 'Delete',
          icon: DeleteIcon
        })
      }
      return allowedActions
    },
    [actions]
  )

  const renderRow = (row: TaskRow) => {
    // mobile columns
    const { title, startDateTime, owner, dealName, completed } = row
    const isOverdue = moment(startDateTime).isBefore(moment()) && !completed
    const timeOptions = startDateTime ? generateTimeOptions(Number(startDateTime)) : []
    // only show the h:mma part if there is a time component to the date (ie its not 12:00am)
    const showTime =
      timeOptions.length > 0 && timeOptions.some((option) => option.value.valueOf() === Number(startDateTime))

    const dateLabel = startDateTime
      ? `${moment(startDateTime).format(`MMM D${showTime ? ' YYYY, h:mma' : ', YYYY'}`)}`
      : null
    const dealLabel = pathname.includes('deals') || !dealName ? '' : dealName
    const templateLabel = pathname.includes('deals') ? null : row.template?.templateLabel

    const denseLabel = []

    if (dateLabel) {
      denseLabel.push(
        <span
          key="dateLabel"
          className={classNames({
            [classes.overdueText]: isOverdue && !completed
          })}
        >
          {dateLabel}
        </span>
      )
    }

    if (dealLabel) {
      if (denseLabel.length > 0) {
        denseLabel.push(<span key="separator-deal">{' · '}</span>)
      }
      denseLabel.push(<span key="dealLabel">{dealLabel}</span>)
    }

    if (templateLabel) {
      if (denseLabel.length > 0) {
        denseLabel.push(<span key="separator-label">{' · '}</span>)
      }
      denseLabel.push(<span key="templateLabel">{templateLabel}</span>)
    }

    if (isDense) {
      return (
        <TableRow key={row._id} data-testid={`task-${row._id}`} onClick={(ev) => handleDenseRowClick(ev, row)}>
          <motion.div
            key={row._id}
            initial={{ opacity: 1 }}
            animate={{ opacity: pendingRemoval[row._id] ? 0 : 1 }}
            transition={{
              opacity: pendingRemoval[row._id] ? { duration: 2 } : { duration: 0 }
            }}
            layout
          >
            <Grid
              container
              className={classes.taskDocItem}
              style={onlyEditOwn && !row.isOwner ? { cursor: 'default' } : undefined}
            >
              <Grid item style={denseLabel.length > 0 ? { alignSelf: FLEX_START } : {}}>
                <Checkbox
                  className={classes.checkbox}
                  checked={completed}
                  onClick={(ev) => ev.stopPropagation()}
                  onChange={async (ev, checked) => {
                    await rowCheckClick(ev, checked, row)
                  }}
                />
              </Grid>
              <Grid item xs style={{ paddingRight: '8px' }}>
                <Typography
                  variant={'body1'}
                  style={{ overflowWrap: 'anywhere' }}
                  className={classNames({ [classes.completedText]: completed })}
                >
                  {title}
                </Typography>
                {denseLabel.length > 0 && (
                  <Typography
                    variant={'subtitle2'}
                    color="textSecondary"
                    className={classNames({ [classes.completedText]: completed })}
                  >
                    {denseLabel}
                  </Typography>
                )}
              </Grid>
              {owner && (owner.app_metadata?.active ?? true) && (
                <Grid item>
                  <EstimatorAvatar
                    userPictures={userPictures}
                    user={{
                      id: removeAuth0Prefix(owner?.user_id),
                      firstName: (owner?.name ?? '').split(' ')[0],
                      lastName: (owner?.name ?? '').split(' ')[1]
                    }}
                    width="36px"
                    height="36px"
                    fontSize="0.9rem"
                    avatarColor={getActivityColor(owner?.user_metadata?.color)}
                  />
                </Grid>
              )}
            </Grid>
          </motion.div>
        </TableRow>
      )
    }

    // normal columns
    return (
      <motion.tr
        key={row._id}
        data-testid={`quote-${row._id}`}
        className={classes.tableRow}
        style={onlyEditOwn && !row.isOwner ? { cursor: 'default' } : undefined}
        initial={{ opacity: 1 }}
        animate={{ opacity: pendingRemoval[row._id] ? 0 : 1 }}
        transition={{
          opacity: pendingRemoval[row._id] ? { duration: 2 } : { duration: 0 }
        }}
        layout
        onClick={() => {}}
      >
        <TableCell padding="none" className={classes.fullCheckbox}>
          <Checkbox
            checked={row.completed}
            onChange={(ev, checked) => {
              rowCheckClick(ev, checked, row)
            }}
          />
        </TableCell>

        {columns.map((column) => {
          if (!column.selected) {
            return null
          }
          return (
            <TableCell
              key={String(column.field)}
              align={column.align}
              className={classNames({
                [classes.completedText]: row.completed
              })}
              onClick={(ev) => handleRowClick(ev, row)}
              style={
                {
                  // display: 'flex',
                  // paddingTop: 0
                }
              }
            >
              {getTaskTableColumn({ key: column.field as any, row })}
            </TableCell>
          )
        })}

        {Object.values(actions).some(Boolean) && (
          <TableCell align="right">
            <ActionButton
              data-testid="actions"
              actions={rowActions(row)}
              disabled={rowActions(row).length === 0 || trialExpired}
              onActionClick={(ev, action) => handleAction(action, [row])}
            />
          </TableCell>
        )}
      </motion.tr>
    )
  }

  const createSortHandler = (property: string) => (event: React.MouseEvent) => {
    if (onRequestSort) {
      // get current sort direction
      const currentSort = getSortDirection(property) || 'asc'

      // flip the sort direction
      const newSort = currentSort === 'asc' ? 'desc' : 'asc'

      onRequestSort(event, property, newSort)
    }
  }

  const getSortDirection = (property: string) => {
    const currentSortDirection = sort && sort.startsWith('-') ? 'desc' : 'asc'
    const currentSortName = sort && sort.startsWith('-') ? sort.slice(1) : sort

    if (currentSortName === property) {
      return currentSortDirection
    }
  }

  function getTaskTableColumn({ key, row, isDense }: TaskTableColumnArgs) {
    switch (key) {
      case 'startDateTime':
        if (!row[key]) {
          return (
            <div
              className={classNames({
                [classes.dueCell]: !isDense,
                [classes.mobileDueCell]: isDense && !isSm,
                [classes.smDueCell]: isSm
              })}
              style={{ display: 'flex', alignItems: FLEX_START }}
            />
          )
        }
        const isCompleted = row.completed
        const isOverdue = moment(row[key]).isBefore(moment())
        const isCurrent = moment(row[key]).isSame(moment(), 'day')
        const timeOptions = generateTimeOptions(Number(row[key]))
        // only show the h:mma part if there is a time component to the date (ie its not 12:00am)
        const showTime =
          timeOptions.length > 0 && timeOptions.some((option) => option.value.valueOf() === Number(row[key]))

        return (
          <div
            className={classNames({
              [classes.overdueText]: !isCompleted && isOverdue,
              [classes.dueCell]: !isDense,
              [classes.mobileDueCell]: isDense && !isSm,
              [classes.smDueCell]: isSm
            })}
            style={{ display: 'flex', alignItems: FLEX_START }}
          >
            {!isDense || (isSm && <CalendarTodayIcon className={classes.calendarIcon} />)}
            {isCurrent && isDense && !isSm && <div>Today</div>}
            {isCurrent
              ? isSm
                ? showTime
                  ? `Today ${moment(row[key]).format('- h:mma')}`
                  : 'Today'
                : showTime
                  ? `${moment(row[key]).format(' h:mma')}`
                  : ''
              : showTime
                ? moment(row[key]).format('MMMM D, YYYY - h:mma')
                : moment(row[key]).format('MMMM D, YYYY')}
          </div>
        )
      case 'title':
        return (
          <>
            <div
              className={classNames(classes.cellText, {
                [classes.titleCell]: !isDense
              })}
            >
              {row[key] || ''}
            </div>
            {row.template?.templateLabel && (
              <Typography variant="caption" color="textSecondary">
                {row.template.templateLabel}
              </Typography>
            )}
          </>
        )
      case 'contactName':
      case 'contactPreferred':
      case 'contactPhone':
      case 'dealName':
      case 'dealAddress':
        return <div className={classes.cellText}>{row[key] || ''}</div>
      case 'contactEmail':
        return <div className={`${classes.cellText} ${classes.emailCell}`}>{row[key] || ''}</div>

      case OWNER_FIRST_NAME_FIELD:
        // Tasks owned by a deactivated user (or with no owner) render unassigned
        // regardless of the deactivation choice — the task is effectively orphaned.
        if (!row?.owner || row.owner.app_metadata?.active === false) return null
        return (
          <EstimatorAvatar
            userPictures={userPictures}
            user={{
              id: removeAuth0Prefix(row?.owner?.user_id),
              firstName: (row?.owner?.name ?? '').split(' ')[0],
              lastName: (row?.owner?.name ?? '').split(' ')[1]
            }}
            width="36px"
            height="36px"
            fontSize="0.9rem"
            avatarColor={getActivityColor(row?.owner?.user_metadata?.color)}
          />
        )

      default:
        return row[key].toString()
    }
  }

  return (
    <Card className={classNames(classes.card, classes.taskTableCard)} data-testid={'task-list'}>
      {title && (
        <div className={classes.titleContainer}>
          <Typography variant="h3">{title}</Typography>
          {handleCreate && (
            <Button
              variant="hoverUnderline"
              color="primary"
              icon={AddIcon}
              onClick={handleCreate}
              className="tour-task-table-new-button"
            >
              New
            </Button>
          )}
        </div>
      )}
      <div className={classes.cardContents}>
        <ResultsTable
          classes={{ root: classes.root }}
          noResultsMessage={noResultsMessage}
          columns={
            !isDense && (
              <TableRow>
                {/* Empty one for checkbox */}
                <TableCell />
                {columns.map((column) => {
                  if (!column.selected) {
                    return null
                  }
                  const sortable = !!sort && !!column.sortable && !!onRequestSort
                  return (
                    <TableCell
                      key={column.field}
                      align={column.align || 'left'}
                      fadeWhenInactive
                      sortable={sortable}
                      sortDirection={getSortDirection(column.field)}
                      onClick={sortable ? createSortHandler(column.field) : null}
                      style={{ minWidth: column.minWidth || undefined }}
                      nowrap={true}
                    >
                      {column.name}
                    </TableCell>
                  )
                })}
                {Object.values(actions).some(Boolean) && (
                  <TableCell align="right">
                    {allowCustomColumns && (
                      <ResultsTableColumnSelect columns={columns} onConfirm={handleColumnsChange} disabled={loading} />
                    )}
                  </TableCell>
                )}
              </TableRow>
            )
          }
          renderRow={renderRow}
          rows={rows}
          rowsPerPage={rowsPerPage}
          loading={loading}
          pagesLoadedIndividually
          disablePagination={otherProps.disablePagination}
          {...otherProps}
          onPageChange={handlePageChange}
        />
        <PreviewPopper
          key={selectedTask?._id}
          anchorEl={anchorEl}
          onClose={handleClose}
          onConfirm={() => handleEditTask(selectedTask?._id)}
          onCancel={() => handleDeleteTask(selectedTask?._id)}
          title="Task Details"
          confirmLabel="Edit"
          cancelLabel="Delete"
          details={[
            mobile &&
              selectedTask?.title && {
                title: 'Task',
                value: selectedTask.title
              },
            mobile &&
              selectedTask?.startDateTime && {
                title: 'Due Date',
                value: moment(selectedTask.startDateTime).format('MMMM D, YYYY - h:mma')
              },
            selectedTask?.dealName &&
              !onPage(selectedTask.dealId) && {
                title: 'Deal',
                value: selectedTask.dealName,
                link: onlyEditOwn
                  ? undefined
                  : () => {
                      window.location.href = escapeHref(`/deals/edit/${selectedTask.dealId}`)
                    }
              },
            selectedTask?.contactName &&
              !onPage(selectedTask.contactId) && {
                title: 'Contact',
                value: selectedTask.contactName,
                link: onlyEditOwn
                  ? undefined
                  : () => {
                      window.location.href = escapeHref(`/people/contact/${selectedTask.contactId}`)
                    }
              },
            selectedTask?.companyName &&
              !onPage(selectedTask.companyId) && {
                title: 'Company',
                value: selectedTask.companyName,
                link: onlyEditOwn
                  ? undefined
                  : () => {
                      window.location.href = escapeHref(`/people/companies/${selectedTask.companyId}`)
                    }
              },
            selectedTask?.contactEmail && {
              title: 'Email',
              value: selectedTask.contactEmail,
              link: () => {
                window.location.href = escapeHref(`mailto:${selectedTask.contactEmail}`)
              },
              star: selectedTask.contactPreferred === 'Email',
              canCopy: true
            },
            selectedTask?.contactPhone && {
              title: 'Phone',
              value: selectedTask.contactPhone,
              star: ['Phone', 'Text'].includes(selectedTask.contactPreferred),
              link: () => {
                window.location.href = escapeHref(`tel:${selectedTask.contactPhone}`)
              },
              canCopy: true
            },
            mobile &&
              selectedTask?.owner && {
                title: 'User',
                value: selectedTask.owner?.name,
                user: users.find(
                  (user) => removeAuth0Prefix(user.user_id) === removeAuth0Prefix(selectedTask.owner.user_id)
                )
              },
            selectedTask?.notes && {
              title: 'Description',
              value: selectedTask.notes,
              html: striptags(selectedTask.notes) !== selectedTask.notes ? true : false
            }
          ].filter(Boolean)}
        />
      </div>
    </Card>
  )
}

function mapTaskToRow(
  options: OptionsDocument,
  task: Partial<Task & { dealName?: string; dealAddress?: string; jobAddress?: DealDocument['jobAddress'] }>,
  user: Auth0UserWithClaims,
  users: { [key: string]: User }
): TaskRow {
  if (!task) {
    return null
  }
  const isAssigned = isUserAssigned(task, user.user_id)
  const isOwner = isUserOwner(task, user.user_id)

  // Format deal address from jobAddress or dealAddress
  const dealAddress =
    task?.dealAddress ||
    (task?.jobAddress && Object.keys(task.jobAddress).length
      ? `${task.jobAddress?.address ?? ''}${task.jobAddress?.city ? `, ${task.jobAddress?.city}` : ''}`
      : '')

  return {
    _id: task._id,
    _rev: task._rev,
    title: `${task?.title}`,
    contactEmail: task?.contact?.email,
    startDateTime: task.startDateTime ? moment(task.startDateTime) : null,
    completed: task?.completed,
    contactId: task?.contact?._id,
    contactName: formatName(task?.contact),
    contactPreferred: (contactMethods || []).find(
      (method) => method.value === (task?.contact?.preferred_contact_method ?? '')
    )?.label,
    contactPhone: formatPhoneNumber({
      options,
      phoneNumber: task?.contact?.phone_number,
      extension: task?.contact?.phone_ext
    }),
    companyName: task?.contact?.company,
    companyId: task?.contact?.companyDocId,
    isAssigned,
    isOwner,
    owner: users?.[removeAuth0Prefix(task?.owner?.id)] ?? null,
    updated: task.updated?.toString() ?? task.created?.toString(),
    dealId: task?.dealId ?? '',
    dealName: task?.dealName ?? '',
    dealAddress: dealAddress || '',
    notes: task?.notes,
    template: task?.template?.templateLabel ? { templateLabel: task.template.templateLabel } : null
  }
}

interface TaskTableColumnArgs {
  key: string
  row: TaskRow
  isDense?: boolean
}

export default TaskTable
